import React, { useContext, useEffect } from 'react';
import { useMatchMakingStore } from '../common/store';
import { Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import ScanIcon from '../common/ScanIcon';
import { useIntl } from 'react-intl';
import translations from 'utils/translations';
import ReportList from '../common/ReportList';
import { useNetwork } from 'services/network';
import { LoaderCountContext } from 'context/LoaderV5';
import { listMatchMakingReports } from '../common/utils';

const ReportsTab = () => {
  // Hooks
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const intl = useIntl();
  const i18n = translations.MatchMakingV2(intl).REPORTS;
  const i18nLegacy = translations.Analytics(intl).page.fair.dialog;
  const network = useNetwork();
  const { setLoaderActive } = useContext(LoaderCountContext);
  const [reports, loggedUser] = useMatchMakingStore((state) => [
    state.reports,
    state.loggedUser,
  ]);

  // Effects
  useEffect(() => {
    const fetchMatchMakingUsers = async () => {
      try {
        showLoader();
        const { report: reportsRes } = await listMatchMakingReports({
          network,
          id: loggedUser.federationId,
        });
        storeMatchMakingReports(reportsRes);
      } catch (error) {
        storeMatchMakingReports([]);
      } finally {
        hideLoader();
      }
    };
    if (!reports && loggedUser?.federationId) {
      fetchMatchMakingUsers();
    }
  }, [loggedUser]);

  // Functions
  const showLoader = () => setLoaderActive(true);
  const hideLoader = () => setLoaderActive(false);
  const storeMatchMakingReports = (array) => {
    useMatchMakingStore.setState({
      reports: array,
    });
  };

  const closeAlert = (sev) =>
    useMatchMakingStore.setState({ alertData: { severity: sev } });

  const openAlert = ({ title, message = '', sev }) => {
    useMatchMakingStore.setState({
      alertData: {
        title,
        message,
        onClose: () => closeAlert(sev),
        severity: sev,
      },
    });
  };

  const handleDownloadCsv = async () => {
    try {
      showLoader();
      const params = {
        customMethod: 'get',
        param: 'report',
        salesforceId: loggedUser.federationId,
        scanType: 'matchmaking',
      };
      const res = await network.analyticsTicketHandler(params);
      if (!res?.error && res.code !== 4002) {
        window.open(`${res.filePath}`, '_blank');
      } else {
        openAlert({
          title: i18nLegacy.error.downloadReport,
          message: i18nLegacy.error.downloadUpdate,
          sev: 'error',
        });
      }
    } catch (error) {
      openAlert({
        title: i18nLegacy.error.downloadReport,
        message: i18nLegacy.error.downloadUpdate,
        sev: 'error',
      });
    } finally {
      hideLoader();
    }
  };

  const handleGenerateCsv = async () => {
    try {
      showLoader();
      const params = {
        customMethod: 'post',
        param: 'report',
        salesforceId: loggedUser.federationId,
        scanType: 'matchmaking',
      };
      const res = await network.analyticsTicketHandler(params);
      if (!res?.error && res.code !== 4002) {
        openAlert({ title: i18nLegacy.success.createReport, sev: 'success' });
        useMatchMakingStore.setState({
          generateCsvBtn: {
            show: false,
            disabled: false,
          },
          downloadCsvBtn: {
            show: true,
            disabled: false,
          },
          updateCsvBtn: {
            show: true,
            disabled: false,
          },
        });
      } else {
        openAlert({ title: i18nLegacy.error.createReport, sev: 'error' });
      }
    } catch (error) {
      openAlert({ title: i18nLegacy.error.createReport, sev: 'error' });
    } finally {
      hideLoader();
    }
  };

  const NoReportsAvailable = () => (
    <Stack
      alignItems={'center'}
      justifyContent={'center'}
      gap={isSmDown ? '5px' : '15px'}
    >
      <Stack
        alignItems={'center'}
        justifyContent={'center'}
        width={'80px'}
        height={'80px'}
      >
        <ScanIcon />
      </Stack>
      <Typography variant="body2" textAlign={'center'}>
        {i18n.noReportsTitle}
      </Typography>
    </Stack>
  );
  return reports ? (
    <>
      {reports.length ? (
        <ReportList
          onDownloadCsv={handleDownloadCsv}
          onGenerateCsv={handleGenerateCsv}
        />
      ) : (
        <NoReportsAvailable />
      )}
    </>
  ) : (
    <></>
  );
};
export default ReportsTab;
